import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, PaperPlaneTilt, EnvelopeSimple, GoogleLogo } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const AI_BG = "https://images.pexels.com/photos/17485657/pexels-photo-17485657.png";

export default function Login() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await api.post("/auth/request-link", {
        email,
        redirect_base: window.location.origin,
      });
      setSent(true);
      setDevLink(res.data.magic_link || "");
      toast.success("Sign-in link sent to your inbox");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send link");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left brand panel — rare dark section */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden bg-[#1A0A05] border-r border-border">
        <div
          className="absolute inset-0 opacity-30"
          style={{ backgroundImage: `url(${AI_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-[#1A0A05]/70" />
        <div className="absolute inset-0 noise-overlay" />
        <div className="relative z-10">
          <span className="font-mono text-xs tracking-[0.3em] uppercase text-primary">Nettpilot AS</span>
        </div>
        <div className="relative z-10 space-y-6">
          <h1 className="font-head text-4xl lg:text-6xl tracking-tight leading-[1.02] text-[#F5F2ED]">
            The onboarding portal for the <span className="text-primary">AI era</span>.
          </h1>
          <p className="text-[#F5F2ED]/70 font-light max-w-md leading-relaxed">
            Access your agreements, documentation, plugin manuals and downloads - built on the Three-Layer Approach™.
          </p>
          <div className="flex gap-6 pt-4 font-mono text-[11px] tracking-widest uppercase text-[#F5F2ED]/60">
            <span>L1 · Human</span>
            <span>L2 · Search</span>
            <span className="text-primary">L3 · AI</span>
          </div>
        </div>
        <div className="relative z-10 font-mono text-[11px] text-[#F5F2ED]/50 tracking-widest">
          25+ YEARS · 1000+ WEBSITES
        </div>
      </div>

      {/* Right auth panel */}
      <div className="flex items-center justify-center p-6 sm:p-12 grid-lines">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md bg-card border border-border p-8 sm:p-10 relative"
          data-testid="login-card"
        >
          <div className="absolute -top-px -left-px w-8 h-8 border-t-2 border-l-2 border-primary" />
          <div className="absolute -bottom-px -right-px w-8 h-8 border-b-2 border-r-2 border-primary" />

          <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-muted-foreground">
            Secure Access
          </span>
          <h2 className="font-head text-3xl font-light tracking-tight mt-3 mb-2">Sign in</h2>
          <p className="text-sm text-muted-foreground mb-8 font-light">
            Enter your email and we'll send you a magic sign-in link. No password required.
          </p>

          {!sent ? (
            <form onSubmit={submit} className="space-y-4">
              <div className="relative">
                <EnvelopeSimple
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  data-testid="login-email-input"
                  type="email"
                  required
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rounded-none h-12 pl-10 bg-secondary border-border font-mono text-sm focus-visible:ring-primary focus-visible:border-primary"
                />
              </div>
              <Button
                data-testid="login-submit-button"
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-none bg-primary hover:bg-primary/90 text-primary-foreground font-semibold tracking-wide group transition-colors"
              >
                {loading ? "Sending…" : "Send magic link"}
                <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </form>
          ) : (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5" data-testid="login-sent-state">
              <div className="flex items-center gap-3 border border-border bg-secondary p-4">
                <PaperPlaneTilt size={22} className="text-primary shrink-0" />
                <p className="text-sm text-muted-foreground font-light">
                  Check <span className="text-foreground font-medium">{email}</span> for your sign-in link.
                </p>
              </div>
              {devLink && (
                <a
                  href={devLink}
                  data-testid="login-dev-link"
                  className="block text-center text-xs font-mono tracking-wide border border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground p-3 transition-colors"
                >
                  DEV: open sign-in link →
                </a>
              )}
              <button
                onClick={() => { setSent(false); setDevLink(""); }}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors font-mono tracking-wide"
              >
                ← use a different email
              </button>
            </motion.div>
          )}

          <div className="mt-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>
          <button
            type="button"
            data-testid="google-signin-button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/auth/callback";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="mt-4 w-full h-12 rounded-none border border-border bg-secondary hover:bg-accent flex items-center justify-center gap-3 font-mono text-sm tracking-wide transition-colors"
          >
            <GoogleLogo size={18} weight="bold" className="text-primary" /> Continue with Google
          </button>

          <div className="mt-8 pt-6 border-t border-border">
            <p className="text-[11px] text-muted-foreground font-mono tracking-wide leading-relaxed">
              Demo accounts: nickolass@nettpilot.no (admin), partner@admirate.no, customer@komplett.no, client@bovg.no, lead@example.com
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
